import React from 'react';
import type { SerpGap, SerpGapFilter, SerpGapPosition } from '../../types';

/* Open positions in a keyword's top 10: the page there barely uses the
   keyword in its title, URL, H1, first paragraph (and snippet, if turned on).
   Judged on the backend by your keyword settings (keywords/assessment.py);
   this file only shows it and filters on it. */

export const SERP_GAP_HELP =
  'Top-10 positions held by a page that barely targets this keyword - in its ' +
  'title, URL, H1 and first paragraph. A page written for the keyword has a ' +
  'real chance to take that position. What counts is set in Settings → Keywords.';

const SPOT_LABEL: Record<string, string> = {
  title: 'title',
  url: 'URL',
  h1: 'H1',
  opening: 'first paragraph',
  snippet: 'snippet',
};

const WHY_LABEL: Record<string, string> = {
  'big site': 'big site',
  'home page': 'home page',
  forum: 'forum thread',
};

export const GAP_ENGINES = ['Google', 'Bing', 'DuckDuckGo', 'Yahoo', 'StartPage', 'Brave'];

export const EMPTY_GAP_FILTER: SerpGapFilter = {
  minOpen: null,
  bestWithin: null,
  engine: null,
};

export function gapFilterActive(f: SerpGapFilter): boolean {
  return f.minOpen != null || f.bestWithin != null || !!f.engine;
}

/** Short words for the active-filter chip, e.g. "2+ open, one in top 3 on Google". */
export function gapFilterLabel(f: SerpGapFilter): string {
  const parts: string[] = [];
  if (f.minOpen != null) parts.push(`${f.minOpen}+ open`);
  if (f.bestWithin != null) parts.push(`open in top ${f.bestWithin}`);
  let label = parts.join(', ') || 'open positions';
  if (f.engine) label += ` on ${f.engine}`;
  return label;
}

function positionLine(p: SerpGapPosition): string {
  const why = p.why ? WHY_LABEL[p.why] : p.big_site ? 'big site' : undefined;
  const mark = p.is_open ? `✓ open${why ? ` (${why})` : ''}` : why ?? '';
  const used = p.used.length
    ? `uses it in ${p.used.map(s => SPOT_LABEL[s] ?? s).join(', ')}`
    : 'uses it nowhere';
  const pct = p.targeting_pct == null ? '' : ` - ${p.targeting_pct}% targeted`;
  return `#${p.position} ${p.domain}: ${used} (${p.used.length}/${p.checked.length})${pct}${mark ? `  ${mark}` : ''}`;
}

function gapTooltip(gaps: SerpGap[]): string {
  const lines = [SERP_GAP_HELP];
  gaps.forEach(g => {
    lines.push('', `${g.search_engine}: ${g.open_positions} open`);
    g.positions.forEach(p => lines.push(`  ${positionLine(p)}`));
    if (g.pages_pending > 0) {
      lines.push(`  ${g.pages_pending} page(s) still downloading`);
    }
  });
  return lines.join('\n');
}

/** Greener the higher the best open position sits. */
function badgeClass(best: number | null): string {
  if (best == null) return 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300';
  if (best <= 3) return 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300';
  if (best <= 6) return 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300';
  return 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300';
}

/** "3 open · best #2" for the engine with the most open positions; the hover
 *  lists every position on every engine. Renders nothing until measured. */
export function SerpGapBadge({ gaps, className = '' }: { gaps?: SerpGap[]; className?: string }) {
  const measured = (gaps ?? []).filter(g => g.positions.length > 0);
  if (measured.length === 0) return null;
  const top = measured[0];
  const text =
    top.best_open_position == null
      ? '0 open'
      : `${top.open_positions} open · best #${top.best_open_position}`;
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-xs rounded cursor-help whitespace-nowrap ${badgeClass(top.best_open_position)} ${className}`}
      title={gapTooltip(measured)}
      aria-label={`${text} on ${top.search_engine}`}
    >
      {text}
      <span className="opacity-70">· {top.search_engine}</span>
    </span>
  );
}

/** Empty means no filter. Anything else is kept to a whole number in
 *  [min, 10], the range the backend accepts; outside it the request fails. */
const numberOrNull = (v: string, min: number): number | null => {
  if (v === '') return null;
  const n = Math.round(Number(v));
  return Number.isFinite(n) ? Math.min(10, Math.max(min, n)) : null;
};

/** The open-position filter inputs. Controlled; the caller decides when to apply. */
export function SerpGapFilterFields({
  value,
  onChange,
  onEnter,
}: {
  value: SerpGapFilter;
  onChange: (next: SerpGapFilter) => void;
  onEnter?: () => void;
}) {
  const set = (patch: Partial<SerpGapFilter>) => onChange({ ...value, ...patch });
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && onEnter) onEnter();
  };
  const inputClass =
    'w-16 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-purple-500';
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1" title={SERP_GAP_HELP}>
        Open positions
      </label>
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <span className="block text-xs text-gray-500 dark:text-gray-400">At least</span>
          <input
            type="number"
            min={0}
            max={10}
            step={1}
            value={value.minOpen ?? ''}
            onChange={e => set({ minOpen: numberOrNull(e.target.value, 0) })}
            onKeyDown={onKeyDown}
            placeholder="e.g. 2"
            className={inputClass}
          />
        </div>
        <div>
          <span className="block text-xs text-gray-500 dark:text-gray-400">One in the top</span>
          <input
            type="number"
            min={1}
            max={10}
            step={1}
            value={value.bestWithin ?? ''}
            onChange={e => set({ bestWithin: numberOrNull(e.target.value, 1) })}
            onKeyDown={onKeyDown}
            placeholder="e.g. 3"
            className={inputClass}
          />
        </div>
        <div>
          <span className="block text-xs text-gray-500 dark:text-gray-400">Engine</span>
          <select
            value={value.engine ?? ''}
            onChange={e => set({ engine: e.target.value || null })}
            className="border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-purple-500"
          >
            <option value="">Any engine</option>
            {GAP_ENGINES.map(e => (
              <option key={e} value={e}>{e}</option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}
